import React, { memo, useCallback, useEffect, useRef } from 'react';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';

import {
  descriptionStyles,
  footerStyles,
  getOptionRowStyles,
  getTypeChipStyles,
  groupHeaderStyles,
  groupHintStyles,
  headerStyles,
  keycapStyles,
  labelStyles,
  listStyles,
  nameStyles,
  popoverStyles,
  scopeCollectionStyles,
  scopeIteratorStyles,
  scopeRowStyles,
  signatureStyles,
  typedChipStyles,
} from './AutocompletePopover.styles';

const renderHighlightedName = parts =>
  parts.map((part, index) => {
    const key = `${index}-${part.text}`;

    if (part.match) {
      return <mark key={key}>{part.text}</mark>;
    }

    return <React.Fragment key={key}>{part.text}</React.Fragment>;
  });

const getOptionId = index => `autocomplete-option-${index}`;

const OptionRow = memo(({ option, isActive, onHover, onSelect }) => {
  const rowRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    onHover(option.index);
  }, [onHover, option.index]);

  const handleMouseDown = useCallback(
    event => {
      event.preventDefault();
      onSelect(option.index);
    },
    [onSelect, option.index],
  );

  useEffect(() => {
    if (isActive && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isActive]);

  return (
    <Box
      aria-selected={isActive}
      id={getOptionId(option.index)}
      ref={rowRef}
      role="option"
      sx={getOptionRowStyles(isActive)}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
    >
      <Box component="span" sx={getTypeChipStyles(option.chip)}>
        {option.chipLabel}
      </Box>
      <Box sx={labelStyles}>
        <Box component="span" sx={nameStyles}>
          {renderHighlightedName(option.nameParts)}
        </Box>
        {option.signature ? (
          <Box component="span" sx={signatureStyles}>
            {option.signature}
          </Box>
        ) : null}
        {option.description ? (
          <Box component="span" sx={descriptionStyles}>
            {option.description}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
});

OptionRow.displayName = 'OptionRow';

OptionRow.propTypes = {
  option: PropTypes.shape({
    index: PropTypes.number.isRequired,
    chip: PropTypes.string.isRequired,
    chipLabel: PropTypes.string.isRequired,
    nameParts: PropTypes.arrayOf(
      PropTypes.shape({ text: PropTypes.string.isRequired, match: PropTypes.bool.isRequired }),
    ).isRequired,
    signature: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  onHover: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const AutocompletePopover = ({ groups, activeIndex, anchor, typed, scope, onHover, onSelect }) => (
  <Box sx={popoverStyles(anchor)}>
    <Box sx={headerStyles}>
      Completing
      <Box component="span" sx={typedChipStyles}>
        {typed || '…'}
      </Box>
    </Box>

    {scope ? (
      <Box sx={scopeRowStyles}>
        Scope: each
        <Box component="span" sx={scopeCollectionStyles}>
          {scope.collection}
        </Box>
        (iterator
        <Box component="code" sx={scopeIteratorStyles}>
          {scope.iterator}
        </Box>
        ) — only item fields &amp; functions shown
      </Box>
    ) : null}

    <Box aria-activedescendant={getOptionId(activeIndex)} role="listbox" sx={listStyles}>
      {groups.map(group => (
        <Box key={group.title}>
          <Box sx={groupHeaderStyles}>
            {group.title}
            {group.hint ? (
              <Box component="span" sx={groupHintStyles}>
                {group.hint}
              </Box>
            ) : null}
          </Box>
          {group.options.map(option => (
            <OptionRow
              isActive={option.index === activeIndex}
              key={option.index}
              option={option}
              onHover={onHover}
              onSelect={onSelect}
            />
          ))}
        </Box>
      ))}
    </Box>

    <Box sx={footerStyles}>
      <Box component="span" sx={keycapStyles}>
        ↑
      </Box>
      <Box component="span" sx={keycapStyles}>
        ↓
      </Box>
      <Box component="span">move</Box>
      <Box component="span" sx={keycapStyles}>
        ↵
      </Box>
      <Box component="span">insert</Box>
      <Box component="span">·</Box>
      <Box component="span" sx={keycapStyles}>
        esc
      </Box>
      <Box component="span">dismiss</Box>
    </Box>
  </Box>
);

AutocompletePopover.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      options: PropTypes.array.isRequired,
      hint: PropTypes.string,
    }),
  ).isRequired,
  activeIndex: PropTypes.number.isRequired,
  anchor: PropTypes.shape({ top: PropTypes.number, left: PropTypes.number }).isRequired,
  onHover: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  typed: PropTypes.string,
  scope: PropTypes.shape({
    collection: PropTypes.string.isRequired,
    iterator: PropTypes.string.isRequired,
  }),
};

export default memo(AutocompletePopover);
